import React, { useState, useEffect } from 'react';
import { Users, DollarSign, TrendingUp, Activity, FileText, DoorClosedIcon, SquareDashedBottomIcon } from 'lucide-react';
import { getDashboardContent } from '../utils/dashboardContentApi';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const { user, userSites } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedCount, setProcessedCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    title: '',
    description: '',
    meta: []
  });

  // Get site_id from user login data
  const currentSiteId = userSites?.site_id || user?.site_id || null;

  // Fetch processed count
  useEffect(() => {
    const fetchProcessedCount = async () => {
      if (!currentSiteId) {
        console.warn('⚠️ No site_id available, skipping processed count fetch');
        return;
      }

      try {
        console.log('🔄 Fetching processed count for site_id:', currentSiteId);
        const response = await apiCall('/surveys/processed-count', {
          method: 'GET',
          params: { site_id: currentSiteId }
        });

        if (response.success && response.data.data) {
          const count = response.data.data.count || response.data.data?.count || response.data.data.processed_count || 0;
          console.log('✅ Processed count loaded:', count);
          setProcessedCount(count);
        } else {
          console.warn('⚠️ Failed to fetch processed count:', response.error);
        }
      } catch (err) {
        console.error('❌ Error fetching processed count:', err);
      }
    };

    fetchProcessedCount();
  }, [currentSiteId]);

  // Fetch user count
  useEffect(() => {
    const fetchUserCount = async () => {
      if (!currentSiteId) {
        console.warn('⚠️ No site_id available, skipping user count fetch');
        return;
      }

      try {
        console.log('🔄 Fetching user count for site_id:', currentSiteId);
        const response = await apiCall('/surveys/user-count-by-site', {
          method: 'GET',
          params: { site_id: currentSiteId }
        });

        if (response.success && response.data.data) {
          const count = response.data.data.count || response.data.data?.count || response.data.data.active_users || 0;
          console.log('✅ User count loaded:', response);
          setUserCount(count);
        } else {
          console.warn('⚠️ Failed to fetch user count:', response.error);
        }
      } catch (err) {
        console.error('❌ Error fetching user count:', err);
      }
    };

    fetchUserCount();
  }, [currentSiteId]);

  // Fetch dashboard content on component mount
  useEffect(() => {
    const fetchDashboardContent = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('🔄 Fetching dashboard content...');
        const response = await getDashboardContent();
        
        if (response.success && response.data) {
          console.log('✅ Dashboard content loaded:', response.data);
          setDashboardData({
            title: response.data.data.title || '',
            description: response.data.data.description || '',
            meta: Array.isArray(response.data.data.meta) ? response.data.data.meta : []
          });
        } else if (!response.success) {
          console.log('ℹ️ No dashboard content found, using defaults');
          // Keep default empty values
        }
      } catch (err) {
        console.error('❌ Error fetching dashboard content:', err);
        setError('Failed to load dashboard content');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardContent();
  }, []);

  const stats = [
    {
      name: 'NUMBER OF RESPONSES',
      value: processedCount.toString(),
      change: '+12%',
      changeType: 'positive',
      icon: SquareDashedBottomIcon,
      color: 'bg-blue-500'
    },

    {
      name: 'NUMBER OF USERS',
      value: userCount.toString(),
      change: '+23%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-purple-500'
    },

  ];

  const recentActivity = [
    { id: 1, user: 'John Doe', action: 'created a new survey', time: '2 minutes ago' },
    { id: 2, user: 'Jane Smith', action: 'updated user profile', time: '5 minutes ago' },
    { id: 3, user: 'Mike Johnson', action: 'submitted survey response', time: '10 minutes ago' },
    { id: 4, user: 'Sarah Wilson', action: 'updated survey settings', time: '15 minutes ago' },
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading dashboard content...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="card">
        <div 
          className="text-4xl font-bold text-gray-900 mb-2"
          dangerouslySetInnerHTML={{ 
            __html: dashboardData.title || '<h1>Welcome to MSQ Agency+.</h1>' 
          }}
        />
        <br/>
      <div className="text-gray-600 space-y-1 max-w-4xl" >
        <div 
          className="text-gray-900"
          dangerouslySetInnerHTML={{
            __html: dashboardData.description || '<p>Welcome back! Here\'s what\'s happening with your business today.</p>' 
          }}
        />
        </div>

        
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
            {/* <div className="mt-4 flex items-center">
              <span className={`text-sm font-medium ${
                stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.change}
              </span>
              <span className="text-sm text-gray-600 ml-2">from last month</span>
            </div> */}
          </div>
        ))}
      </div>


      {/* Charts and Activity 
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Overview</h3>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Chart component would go here</p>
              <p className="text-sm text-gray-400">Integrate with Chart.js or Recharts</p>
            </div>
          </div>
        </div>

     
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.user}</span> {activity.action}
                  </p>
                  <p className="text-sm text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              View all activity →
            </button>
          </div>
        </div>
      </div>
      */}
      {/* Quick Actions 
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Jump right in</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="btn-primary flex items-center justify-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Add New User</span>
          </button>
          <button className="btn-primary flex items-center justify-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Create Survey</span>
          </button>
          <button className="btn-primary flex items-center justify-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>View Reports</span>
          </button>
        </div>
      </div>
        */}
      {/* Dashboard Meta Cards */}
      {dashboardData.meta && dashboardData.meta.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Jump right in</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboardData.meta.map((meta, index) => {
              const cardContent = (
                <>
                  {meta.meta_attach && (
                    <div className="mt-2">
                      <img 
                        src={meta.meta_attach} 
                        alt={meta.meta_key}
                        className="h-160 w-160 object-cover rounded-md border border-gray-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-xl font-medium text-gray-700 mt-4">{meta.meta_key}</h4>
                   
                  </div>
                  <div 
                    className="text-sm text-gray-600 mb-4"
                    dangerouslySetInnerHTML={{ __html: meta.meta_value || '' }}
                  />
                </>
              );

              return (
                <div key={index} className={`border border-gray-200 rounded-lg p-4 bg-gray-50 transition-all duration-200 ${
                  meta.meta_link ? 'hover:bg-gray-100 hover:shadow-md cursor-pointer' : ''
                }`}>
                  {meta.meta_link ? (
                    <a
                      href={meta.meta_link}
                      target={meta.meta_link_target ? '_blank' : '_self'}
                      rel={meta.meta_link_target ? 'noopener noreferrer' : ''}
                      className="block w-full h-full"
                    >
                      {cardContent}
                    </a>
                  ) : (
                    cardContent
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
